/* eslint-disable react/no-unused-prop-types */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Pane, Loading, Button, Layout } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { AppIcon, IfPermission } from '@folio/stripes-core';
import EmptyData from '../components/common/emptyData/EmptyData';
import useFavoriteReportsAPI from '../hooks/useFavoriteReportsAPI';
import useReport from '../hooks/useReport';
import FavoriteReportCard from '../components/favorites/FavoriteReportCard';
import FavoritesFilter from '../components/favorites/FavoritesFilter';
import ReorderFavoritesModal from '../components/favorites/ReorderFavoritesModal';
import { getLocalizedValue } from '../helpers/localizationUtils';

function PrefferedReportsRoute(props) {
  const { favoriteReports, isLoading, removeFavorite, updateFavorite } = useFavoriteReportsAPI();
  const { reports: allReports, isLoading: reportsLoading } = useReport();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showReorderModal, setShowReorderModal] = useState(false);

  const favoriteReportsWithDetails = favoriteReports
    .map(fav => {
      const report = allReports?.find(r => r.id === fav.reportId);
      return report ? { ...report, favoriteId: fav.id, tags: fav.tags, notes: fav.notes, displayOrder: fav.displayOrder } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  // Get all unique tags
  const availableTags = useMemo(() => {
    const tags = new Set();
    favoriteReportsWithDetails.forEach(report => {
      report.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  }, [favoriteReportsWithDetails]);

  // Filter favorites
  const filteredFavorites = useMemo(() => {
    return favoriteReportsWithDetails.filter(report => {
      const reportName = getLocalizedValue(report.name);
      const reportDesc = getLocalizedValue(report.desc);
      const matchesSearch = !searchTerm ||
        reportName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reportDesc?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTag = !selectedTag || report.tags?.includes(selectedTag);

      return matchesSearch && matchesTag;
    });
  }, [favoriteReportsWithDetails, searchTerm, selectedTag]);

  const handleRemove = async (favoriteId) => {
    await removeFavorite.mutateAsync(favoriteId);
  };

  const handleReorder = async (orderedIds) => {
    // Update display order for each favorite
    const updates = orderedIds.map((id, index) => 
      updateFavorite.mutateAsync({ 
        favoriteId: id, 
        updates: { displayOrder: index } 
      })
    );
    await Promise.all(updates);
  };

  const handleReportClick = (reportId) => {
    props.history.push(`/reports/reports-list/${reportId}`);
  };

  if (isLoading || reportsLoading) {
    return (
      <Pane
        id="pane-preffered-reports"
        appIcon={<AppIcon app="reports" />}
        defaultWidth="fill"
        paneTitle={
          <FormattedMessage
            id="ui-reports.panes.prefferedReports.title"
            defaultMessage="Preffered Reports"
          />
        }
      >
        <Loading />
      </Pane>
    );
  }

  if (favoriteReportsWithDetails.length === 0) {
    return (
      <Pane
        id="pane-preffered-reports"
        appIcon={<AppIcon app="reports" />}
        defaultWidth="fill"
        fluidContentWidth
        noOverflow
        padContent={false}
        paneTitle={
          <FormattedMessage
            id="ui-reports.panes.prefferedReports.title"
            defaultMessage="Preffered Reports"
          />
        }
      >
        <EmptyData />
      </Pane>
    );
  }

  return (
    <Pane
      id="pane-preffered-reports"
      appIcon={<AppIcon app="reports" />}
      defaultWidth="fill"
      fluidContentWidth
      paneTitle={
        <FormattedMessage
          id="ui-reports.panes.prefferedReports.title"
          defaultMessage="Preffered Reports"
        />
      }
      lastMenu={
        <IfPermission perm="ui-reports.favorite-reports.manage">
          <Button onClick={() => setShowReorderModal(true)}>
            <FormattedMessage id="ui-reports.favorites.reorder" defaultMessage="Reorder" />
          </Button>
        </IfPermission>
      }
    >
      <FavoritesFilter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedTag={selectedTag}
        onTagChange={setSelectedTag}
        availableTags={availableTags}
      />

      <Layout className="padding-all-gutter">
        {filteredFavorites.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            <FormattedMessage
              id="ui-reports.favorites.noResults"
              defaultMessage="No favorites match your search"
            />
          </div>
        ) : (
          filteredFavorites.map(report => (
            <FavoriteReportCard
              key={report.id}
              report={report}
              onRemove={handleRemove}
              onClick={handleReportClick}
            />
          ))
        )}
      </Layout>

      <ReorderFavoritesModal
        open={showReorderModal}
        onClose={() => setShowReorderModal(false)}
        favorites={favoriteReportsWithDetails}
        onReorder={handleReorder}
      />
    </Pane>
  );
}

PrefferedReportsRoute.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.object
};

export default PrefferedReportsRoute;
